import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000'
const SESSION_TIMEOUT_MS = 1 * 60 * 60 * 1000;
const ACTIVITY_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const SESSION_EXPIRED_EVENT = 'inventory-app-session-expired';

const readError = async (res, fallback) => {
  try {
    const data = await res.json();
    return data?.error || data?.message || fallback;
  } catch (_) {
    return fallback;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  const expireTimerRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());
  const lastRefreshAtRef = useRef(0);
  const refreshInFlightRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const clearExpireTimer = useCallback(() => {
    if (expireTimerRef.current) {
      window.clearTimeout(expireTimerRef.current);
      expireTimerRef.current = null;
    }
  }, []);

  const markSessionFresh = useCallback(() => {
    const now = Date.now();
    lastActivityAtRef.current = now;
    lastRefreshAtRef.current = now;
  }, []);

  const expireSession = useCallback(() => {
    clearExpireTimer();
    refreshInFlightRef.current = null;
    setUser(null);
  }, [clearExpireTimer]);

  const scheduleSessionExpiry = useCallback(() => {
    clearExpireTimer();
    const expiresAt = lastActivityAtRef.current + SESSION_TIMEOUT_MS;
    const delay = Math.max(0, expiresAt - Date.now());
    expireTimerRef.current = window.setTimeout(expireSession, delay);
  }, [clearExpireTimer, expireSession]);

  const refreshSession = useCallback(async ({ force = false } = {}) => {
    if (!force && !userRef.current) return false;

    const now = Date.now();
    if (!force && now - lastRefreshAtRef.current < ACTIVITY_REFRESH_INTERVAL_MS) {
      return true;
    }

    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    refreshInFlightRef.current = fetch(`${API_BASE}/auth/me`, {
      credentials: 'include'
    })
      .then(async (res) => {
        if (!res.ok) {
          expireSession();
          return false;
        }

        const data = await res.json();
        setUser(data.user);
        lastRefreshAtRef.current = Date.now();
        scheduleSessionExpiry();
        return true;
      })
      .catch((err) => {
        console.error('Session refresh failed:', err);
        return false;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });

    return refreshInFlightRef.current;
  }, [expireSession, scheduleSessionExpiry]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          markSessionFresh();
          scheduleSessionExpiry();
        }
      } catch (err) {
        console.error('Session check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [markSessionFresh, scheduleSessionExpiry]);

  useEffect(() => {
    const originalFetch = window.fetch;
    if (!originalFetch) return undefined;

    const shouldIgnoreAuthFailure = (input) => {
      const url = typeof input === 'string' ? input : input?.url || '';
      return url.includes('/auth/login') || url.includes('/auth/logout') || url.includes('/auth/me');
    };

    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const expired = res.status === 401 || (url.includes('/auth/me') && res.status === 403);

      if (expired && !shouldIgnoreAuthFailure(args[0])) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }

      return res;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    const handleExpiredSession = () => expireSession();
    window.addEventListener(SESSION_EXPIRED_EVENT, handleExpiredSession);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpiredSession);
  }, [expireSession]);

  useEffect(() => {
    if (!user) {
      clearExpireTimer();
      return undefined;
    }

    const handleActivity = () => {
      lastActivityAtRef.current = Date.now();
      scheduleSessionExpiry();
      refreshSession();
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'focus'];
    scheduleSessionExpiry();
    events.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [clearExpireTimer, refreshSession, scheduleSessionExpiry, user]);

  const login = async (name, password) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, password })
    });

    if (!res.ok) throw new Error(await readError(res, 'Login failed'));
    const data = await res.json();
    setUser(data.user);
    markSessionFresh();
    scheduleSessionExpiry();
    return data.user;
  };

  const changePassword = async (currentPassword, password) => {
    const res = await fetch(`${API_BASE}/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ currentPassword, password })
    });

    if (!res.ok) throw new Error(await readError(res, 'Failed to change password'));
    const data = await res.json();
    setUser(data.user);
    markSessionFresh();
    scheduleSessionExpiry();
    return data.user;
  };

  const logout = async () => {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    clearExpireTimer();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, changePassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
